import { useEffect, useMemo, useState } from 'react'
import { listCampaigns, subscribeCampaigns } from '../campaigns/data/campaignStore'
import { loadPosts, subscribePosts } from '../social/data/store'
import { buildSampleEmails, buildSampleSms, buildSampleAutomationStats } from '../../../marketing/campaigns/sampleCampaigns'
import { previousRange, sendsFromApi, socialResults } from './data/results'
import { loadSends, loadOverview, loadAutomations, loadVouchers, loadReviewsSince, loadFrontDesk, loadJourneys, loadLoyalty, loadFreeTimesBooked } from './data/load'

// Everything Performance reads, for a range and the chosen locations. Each
// part arrives on its own: `undefined` while it's being read, `null` when it
// can't be counted yet, otherwise its data.
//   frontDesk: whether to read Bailey's front desk (it's on staging only;
//   off, it's null)
export const usePerformance = ({ dispatch, range, salonIds = null, demo = false, frontDesk: withFrontDesk = true }) => {
  const [parts, setParts] = useState({})
  const [groups, setGroups] = useState(() => campaignsFor(salonIds))
  const [posts, setPosts] = useState(loadPosts)
  const prev = useMemo(() => previousRange(range), [range])
  // Far enough back to compare with the period before
  const since = (prev && prev.start) || range.start
  const where = (salonIds || []).join(',')

  useEffect(() => {
    const read = () => setGroups(campaignsFor(salonIds))
    read()
    return subscribeCampaigns(read)
  }, [where]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => subscribePosts(() => setPosts(loadPosts())), [])

  useEffect(() => {
    let alive = true
    const put = key => (value) => { if (alive) setParts(p => ({ ...p, [key]: value })) }
    const fail = key => () => put(key)(null)
    setParts(p => ({ ...p, overview: undefined, automations: undefined, vouchers: undefined, reviews: undefined, frontDesk: withFrontDesk ? undefined : null }))
    loadOverview(dispatch, range, salonIds).then(put('overview'), fail('overview'))
    loadAutomations(dispatch, range, salonIds).then(put('automations'), fail('automations'))
    loadVouchers(range).then(put('vouchers'), fail('vouchers'))
    loadReviewsSince(since, salonIds).then(put('reviews'), fail('reviews'))
    if (withFrontDesk) loadFrontDesk(since).then(put('frontDesk'), fail('frontDesk'))
    return () => { alive = false }
  }, [dispatch, range, where, withFrontDesk]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true
    const put = key => (value) => { if (alive) setParts(p => ({ ...p, [key]: value })) }
    setParts(p => ({ ...p, sends: undefined }))
    loadSends(dispatch, salonIds).then(put('sends'), () => put('sends')([]))
    loadJourneys().then(put('journeys'), () => put('journeys')(null))
    loadLoyalty().then(put('loyalty'), () => put('loyalty')(null))
    return () => { alive = false }
  }, [dispatch, where]) // eslint-disable-line react-hooks/exhaustive-deps

  // Free times posted and then booked, once the posts are known
  const social = useMemo(() => socialResults(posts, range), [posts, range])
  const freeKey = social.freeTimes.map(f => f.id).join(',')
  useEffect(() => {
    let alive = true
    if (!social.freeTimes.length) { setParts(p => ({ ...p, freeTimes: null })); return undefined }
    setParts(p => ({ ...p, freeTimes: undefined }))
    loadFreeTimesBooked(social.freeTimes).then((r) => { if (alive) setParts(p => ({ ...p, freeTimes: r })) }, () => { if (alive) setParts(p => ({ ...p, freeTimes: null })) })
    return () => { alive = false }
  }, [freeKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // The sample account, for someone who hasn't sent anything yet
  const sends = useMemo(() => (demo
    ? [...sendsFromApi(buildSampleEmails(), 'email'), ...sendsFromApi(buildSampleSms(), 'sms')]
    : parts.sends), [demo, parts.sends])
  const automations = demo ? buildSampleAutomationStats() : parts.automations

  return { ...parts, sends, automations, groups, posts, social, range, prev }
}

// Campaigns are kept per location on this device
function campaignsFor(salonIds) {
  const ids = salonIds && salonIds.length ? salonIds : [null]
  return ids.flatMap(id => listCampaigns(id || undefined))
}
